import { flash } from "@xola/ui-kit";
import { shouldAutoExpandRosterNotes } from "common";
import { assign } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { Event, Note, Permission } from "types";
import { api } from "../../../../api/api";
import { populateResource } from "../../../../hooks/helper";
import { useSeller } from "../../../../hooks/useAccount";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { useVisibility } from "../../../../hooks/useVisibility";
import { Accordion } from "../../../Common/Accordion";
import { EventNoteList } from "../../../Common/NoteList";
import { CreateEventNoteModal } from "../../../Event/CreateEventNoteModal";
import { NotesBadge } from "../../Common/NotesBadge";
import { AddNoteButton, NotesHeading } from "../NotesComponents";

interface EventNoteSectionProps {
    event: Event;
    heading?: string;
    className?: string;
}

export const EventNoteSection = ({ event, className, heading }: EventNoteSectionProps) => {
    const invalidate = useInvalidate();

    const seller = useSeller();
    const [visible, { toggle }] = useVisibility(false);

    const shouldAutoExpandNotes = shouldAutoExpandRosterNotes(seller);
    const eventNoteCount = event?.notes?.length ?? 0;

    // TODO: This is a duplicate of code in NotesListPerType. De-duplicate this if you are working in this component
    const { mutateAsync: deleteEventNoteItem } = useMutation(
        async (noteId: ID) => {
            try {
                await api.delete(`/events/${event?.id}/notes/${noteId}`);
            } catch {
                flash.show({ text: "Error on deleting event notes", color: "danger" });
            }
        },
        {
            onSuccess: (_data: void, variables: ID) => {
                if (variables) {
                    flash.success("Successfully deleted event note");
                    const updatedEvent = assign(event, {
                        notes: event?.notes.filter((note: Note) => note.id !== variables),
                    });
                    populateResource("events", updatedEvent);
                }
            },
        },
    );

    const handleAddOnClick = (e: any) => {
        e.stopPropagation();
        toggle();
    };

    const onDeleteEventNotes = async (noteId: ID) => {
        await deleteEventNoteItem(noteId);

        invalidate("purchaseItems");
        invalidate("events", event?.id);
    };

    return (
        <div className={className} data-testid="notes-list-per-type">
            <Accordion defaultOpen={shouldAutoExpandNotes && eventNoteCount > 0}>
                <Accordion.Header>
                    <NotesHeading heading={heading ?? "Notes"} size="large" />

                    <NotesBadge noteCount={eventNoteCount} />
                    {/* TODO: Adding a button inside a button is not correct, try using 'as' prop for below button */}
                    <AddNoteButton permission={Permission.ADD_EVENT_NOTE} onClick={handleAddOnClick} />
                </Accordion.Header>

                <Accordion.Body>
                    {eventNoteCount > 0 && event ? (
                        <div className="ml-4">
                            <EventNoteList className="max-h-max" event={event} onDelete={onDeleteEventNotes} />
                        </div>
                    ) : (
                        <p>No Event Notes</p>
                    )}
                </Accordion.Body>
            </Accordion>

            <CreateEventNoteModal isOpen={visible} events={event ? [event] : []} onClose={() => toggle()} />
        </div>
    );
};
